import { useState } from "react";

import { Alert, Button, FormField, Modal, TextInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { JntLogo } from "@/modules/settings/components/integrations/logos/JntLogo";
import {
  useDisconnectJntMutation,
  useJntConnectionQuery,
  useSaveJntCredentialsMutation,
} from "@/modules/settings/services/useJntConnection";

const JNT_HOTLINE = "15885";
const JNT_SALES_EMAIL = "sales@jtexpress-eg.com";

type Props = {
  storeSlug: string;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * J&T Express connect sheet. Mirrors the Bosta sheet's guided-setup shape:
 * step-by-step instructions for getting an account, then the credential
 * form (validated against J&T before saving).
 */
export function JntSheet({ storeSlug, isOpen, onClose }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { connection } = useJntConnectionQuery({ storeSlug });
  const {
    mutateAsync: save,
    isPending: isSaving,
    errorMessage: saveError,
    reset: resetSave,
  } = useSaveJntCredentialsMutation();
  const { mutateAsync: disconnect, isPending: isDisconnecting } =
    useDisconnectJntMutation();

  const [customerCode, setCustomerCode] = useState("");
  const [password, setPassword] = useState("");
  const [editing, setEditing] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const showForm = !connection || editing;
  const canSubmit =
    !isSaving && customerCode.trim().length >= 3 && password.trim().length >= 3;

  const handleSave = async () => {
    if (!canSubmit) return;
    try {
      await save({
        storeSlug,
        customerCode: customerCode.trim(),
        password: password.trim(),
      });
      toast.success(t(keys.jnt.savedNotice));
      setCustomerCode("");
      setPassword("");
      setEditing(false);
    } catch {
      // errorMessage renders inline below.
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect({ storeSlug });
      toast.success(t(keys.jnt.disconnectedNotice));
      setConfirmingDisconnect(false);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t(keys.errors.generic));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetSave();
        setEditing(false);
        setConfirmingDisconnect(false);
        onClose();
      }}
      size="lg"
      headerOverride={
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center [&_svg]:h-8 [&_svg]:w-8">
            <JntLogo />
          </span>
          <span className="text-[15px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.jnt.sheetTitle)}
          </span>
        </div>
      }
      closeLabel={t(keys.common.close)}
      footerActions={
        showForm ? (
          <Button
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() => void handleSave()}
          >
            {isSaving ? t(keys.common.loading) : t(keys.jnt.saveAction)}
          </Button>
        ) : undefined
      }
      hideFooter={!showForm}
    >
      <div className="mx-auto flex w-full max-w-[520px] flex-col gap-5">
        {connection && !editing ? (
          /* ── Connected view ─────────────────────────────────────────── */
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
              {t(keys.jnt.connectedSummary)}
            </p>
            <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3 py-3">
              <span className="text-xs font-semibold text-[color:var(--text-secondary)]">
                {t(keys.jnt.customerCodeLabel)}
              </span>
              <span
                className="font-mono text-sm font-bold text-[color:var(--text-primary)]"
                dir="ltr"
              >
                {connection.customerCode}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                {t(keys.jnt.updateCredentialsAction)}
              </Button>
              {confirmingDisconnect ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[color:var(--text-secondary)]">
                    {t(keys.jnt.disconnectConfirmBody)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingDisconnect(false)}
                    disabled={isDisconnecting}
                  >
                    {t(keys.common.cancel)}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => void handleDisconnect()}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting
                      ? t(keys.common.loading)
                      : t(keys.jnt.disconnectConfirmCta)}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  {t(keys.jnt.disconnectAction)}
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* ── Guided setup ───────────────────────────────────────────── */
          <>
            <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
              {t(keys.jnt.setupIntro)}
            </p>

            <section className="flex flex-col gap-2">
              <span className="w-fit rounded-full bg-[color:var(--surface-alt)] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[color:var(--text-muted)]">
                {t(keys.jnt.step1Label)}
              </span>
              <h4 className="text-[15px] font-semibold text-[color:var(--text-primary)]">
                {t(keys.jnt.step1Title)}
              </h4>
              <p className="text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
                {t(keys.jnt.step1Body)}
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`tel:${JNT_HOTLINE}`}
                  className="inline-flex h-9 items-center rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-xs font-semibold text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface-alt)]"
                >
                  {t(keys.jnt.callHotlineAction, { hotline: JNT_HOTLINE })}
                </a>
                <a
                  href={`mailto:${JNT_SALES_EMAIL}`}
                  className="inline-flex h-9 items-center rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-xs font-semibold text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface-alt)]"
                >
                  {t(keys.jnt.emailSalesAction)}
                </a>
              </div>
              <p className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
                {t(keys.jnt.step1Hint)}
              </p>
            </section>

            <section className="flex flex-col gap-3">
              <span className="w-fit rounded-full bg-[color:var(--surface-alt)] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[color:var(--text-muted)]">
                {t(keys.jnt.step2Label)}
              </span>
              <h4 className="text-[15px] font-semibold text-[color:var(--text-primary)]">
                {t(keys.jnt.step2Title)}
              </h4>
              <p className="text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
                {t(keys.jnt.step2Body)}
              </p>

              {saveError ? <Alert message={saveError} /> : null}

              <FormField label={t(keys.jnt.customerCodeLabel)} name="jntCustomerCode">
                <TextInput
                  name="jntCustomerCode"
                  dir="ltr"
                  placeholder={t(keys.jnt.customerCodePlaceholder)}
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                />
              </FormField>
              <FormField label={t(keys.jnt.passwordLabel)} name="jntPassword">
                <TextInput
                  name="jntPassword"
                  type="password"
                  dir="ltr"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormField>
              <p className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
                {t(keys.jnt.verifyHint)}
              </p>
              {editing ? (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    resetSave();
                    setEditing(false);
                  }}
                  className="self-start"
                >
                  {t(keys.common.cancel)}
                </Button>
              ) : null}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
